/**
 * i18next Internationalization Configuration
 *
 * Sets up react-i18next for multi-language support in the Andi web frontend.
 *
 * Features:
 * - Language detection (localStorage, browser preference)
 * - Fallback language (English)
 * - Currently supported languages: English (en), Spanish (es)
 *
 * Usage:
 * 1. Import this module in main.tsx before rendering the app
 * 2. Use the useTranslation hook in components:
 *    ```
 *    import { useTranslation } from 'react-i18next';
 *
 *    function MyComponent() {
 *      const { t } = useTranslation();
 *      return <h1>{t('common.welcome')}</h1>;
 *    }
 *    ```
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './en.json';
import es from './es.json';

/** Supported languages in the application */
export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;

/** Type for supported language codes */
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Language display names for UI */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
};

/** Local storage key for language preference */
export const LANGUAGE_STORAGE_KEY = 'rosie-language';

/**
 * Check if a string is a supported language code
 */
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Get the display name for a language code
 */
export function getLanguageName(lang: string): string {
  if (isSupportedLanguage(lang)) {
    return LANGUAGE_NAMES[lang];
  }
  return lang;
}

// Initialize i18next (only if not already initialized)
if (!i18n.isInitialized) {
  void i18n
    // Use language detector to automatically detect user preference
    .use(LanguageDetector)
    // Pass i18n instance to react-i18next
    .use(initReactI18next)
    // Initialize with configuration
    .init({
      // Translation resources
      resources: {
        en: { translation: en },
        es: { translation: es },
      },

      // Fallback language when translation is missing
      fallbackLng: 'en',

      // Supported languages (for type safety and validation)
      supportedLngs: [...SUPPORTED_LANGUAGES],

      // Don't escape values - React already handles XSS protection
      interpolation: {
        escapeValue: false,
      },

      // Language detection configuration
      detection: {
        // Order of detection methods (localStorage first, then browser settings)
        order: ['localStorage', 'navigator'],
        // Cache detected language in localStorage
        caches: ['localStorage'],
        // Use 'rosie-language' as the localStorage key
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      },

      // React-specific options
      react: {
        // Suspend while loading translations (for Suspense support)
        useSuspense: false,
      },

      // Debug mode (disabled in production)
      debug: import.meta.env.DEV,
    });
}

export default i18n;
